/**
 * Sprint 0150 — Host's pending visitor approvals.
 *
 * Lists everyone the gate has punched in to meet THIS user (or
 * escalated to them) that hasn't been approved/rejected yet.
 *
 * The ringer (VisitorApprovalRinger) handles new-arrival pop-ups
 * automatically. This page is the slow-lane review surface for
 * anyone the host has already dismissed off the ringer.
 */
import { useState } from 'react'
import {
  Alert, Avatar, Button, Card, Empty, Input, Modal, Space, Tag,
  Typography, message,
} from 'antd'
import {
  CheckOutlined, CloseOutlined, UserOutlined,
} from '@ant-design/icons'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import dayjs from 'dayjs'

import PageHeader from '@/shared/components/PageHeader'
import { visitorsApi } from '@/features/gate/api'
import type { VisitorLog } from '@/shared/types/master'

const { Text } = Typography

export default function VisitorApprovalsPage() {
  const qc = useQueryClient()
  const [rejecting, setRejecting] = useState<VisitorLog | null>(null)
  const [rejectReason, setRejectReason] = useState('')

  const { data, isLoading } = useQuery({
    queryKey: ['my-pending-visitor-approvals'],
    queryFn: () => visitorsApi.listPendingMyApproval(),
    refetchInterval: 5000,
  })

  const approveMut = useMutation({
    mutationFn: (id: string) => visitorsApi.approve(id),
    onSuccess: () => {
      message.success('Visitor approved — gate notified.')
      qc.invalidateQueries({ queryKey: ['my-pending-visitor-approvals'] })
    },
    onError: () => message.error('Approve failed'),
  })

  const rejectMut = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) =>
      visitorsApi.reject(id, reason),
    onSuccess: () => {
      message.success('Visitor rejected — gate notified.')
      qc.invalidateQueries({ queryKey: ['my-pending-visitor-approvals'] })
      setRejecting(null); setRejectReason('')
    },
    onError: () => message.error('Reject failed'),
  })

  return (
    <div>
      <PageHeader
        title="Pending Visitor Approvals"
        subtitle="Visitors at the gate waiting for your approval. You'll also get a phone-ring pop-up the moment a request lands."
      />
      {data?.length === 0 ? (
        <Empty description="No pending visitor requests for you." />
      ) : (
        <Space direction="vertical" size={12} style={{ width: '100%' }}>
          {data?.map((v) => (
            <Card
              key={v.id}
              size="small"
              loading={isLoading}
              style={v.escalated_at ? {
                borderColor: '#faad14',
                background: '#fffbe6',
              } : undefined}
            >
              {v.escalated_at && (
                <Alert
                  type="warning"
                  showIcon
                  style={{ marginBottom: 8 }}
                  message="Escalated to you — original host didn't respond in 5 min."
                />
              )}
              <Space align="start" size={16} style={{ width: '100%' }}>
                <Avatar
                  src={v.photo_url || undefined}
                  icon={<UserOutlined />}
                  size={72}
                  shape="square"
                  style={{ background: v.photo_url ? undefined : '#bfbfbf' }}
                />
                <div style={{ flex: 1 }}>
                  <Typography.Title level={5} style={{ margin: 0 }}>
                    {v.visitor_name}
                  </Typography.Title>
                  <Text type="secondary">{v.visitor_company || '—'}</Text>
                  <div style={{ marginTop: 6 }}>
                    <Tag color="blue">{(v.purpose || '').replace(/_/g, ' ')}</Tag>
                    <Tag>{v.visitor_pass_no}</Tag>
                    {v.visitor_mobile && (
                      <Tag color="green">{v.visitor_mobile}</Tag>
                    )}
                  </div>
                  <Text type="secondary" style={{ fontSize: 11 }}>
                    Requested {v.approval_requested_at
                      ? dayjs(v.approval_requested_at).fromNow()
                      : '—'}
                  </Text>
                </div>
                <Space>
                  <Button
                    danger
                    icon={<CloseOutlined />}
                    onClick={() => { setRejecting(v); setRejectReason('') }}
                  >
                    Reject
                  </Button>
                  <Button
                    type="primary"
                    icon={<CheckOutlined />}
                    loading={approveMut.isPending && approveMut.variables === v.id}
                    onClick={() => approveMut.mutate(v.id)}
                  >
                    Approve
                  </Button>
                </Space>
              </Space>
            </Card>
          ))}
        </Space>
      )}

      <Modal
        title={rejecting ? `Reject ${rejecting.visitor_name}?` : ''}
        open={!!rejecting}
        onCancel={() => { setRejecting(null); setRejectReason('') }}
        onOk={() => rejecting && rejectMut.mutate({
          id: rejecting.id, reason: rejectReason,
        })}
        okText="Reject"
        okButtonProps={{
          danger: true,
          disabled: !rejectReason.trim(),
          loading: rejectMut.isPending,
        }}
      >
        <Text>Reason will be shown to the gate operator.</Text>
        <Input.TextArea
          rows={3}
          style={{ marginTop: 12 }}
          value={rejectReason}
          onChange={(e) => setRejectReason(e.target.value)}
          placeholder="e.g. Not expecting any visitors. / Wrong person. / Out of office."
        />
      </Modal>
    </div>
  )
}
